import apiClient from '../../apis';

export interface GraphNode {
  id: string;
  type: string;
  config: Record<string, any>;
  position: { x: number; y: number };
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
}

export type TriggerKind = 'manual' | 'scheduled' | 'on_workflow_complete';

export interface TriggerConfig {
  kind: TriggerKind;
  config?: Record<string, any>;
}

export interface Graph {
  nodes: GraphNode[];
  edges: GraphEdge[];
  trigger?: TriggerConfig | null;
  docs_url?: string | null;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  status: string;
  steps: Graph;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  last_run_status?: string | null;
  last_run_at?: string | null;
  last_execution_id?: string | null;
}

export interface WorkflowSummary {
  id: string;
  name: string;
  description: string;
  status: string;
  node_count: number;
  node_types: string[];
  trigger?: TriggerConfig | null;
  trigger_summary?: string;
  docs_url?: string | null;
  last_run_status?: string | null;
  last_run_at?: string | null;
  last_execution_id?: string | null;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface NodeConfigField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'sql' | 'saved_model' | 'template' | 'perspective';
  options?: string[];
  required?: boolean;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  icon?: string;
}

export interface NodeTypeDescriptor {
  type: string;
  category: 'source' | 'transform' | 'destination';
  label: string;
  description: string;
  config_fields: NodeConfigField[];
  min_inputs: number;
  max_inputs: number;
}

export interface NodeRunResult {
  status: 'ok' | 'failed' | 'skipped';
  kind?: 'tabular' | 'document' | 'message';
  elapsed_ms?: number;
  // tabular
  row_count?: number;
  columns?: string[];
  preview?: Record<string, any>[];
  // document
  template_id?: string;
  template_name?: string;
  section_count?: number;
  html?: string;
  has_pdf?: boolean;
  // message
  channel?: string;
  subject?: string;
  body?: string;
  placeholder?: boolean;
  // shared
  error?: string;
  output_ref?: string;
}

export interface Execution {
  id: string;
  workflow_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  step_results: Record<string, NodeRunResult>;
  error?: string | null;
  started_at?: string;
  completed_at?: string | null;
  /**
   * Why this record's contents are not here (#426 r26). The server writes it BESIDE `error: null` — the
   * two travel together, because `null` means "cannot say" and this sentence is what makes it that
   * rather than "there was none" (`workflows/audience.reduced_run`). It is not an error and not derived
   * from one: a run withheld from this reader can be `completed`, and a run that really failed with no
   * text carries no sentence. So it is typed and rendered on its own, and a screen that shows `error`
   * and skips this tells the reader an absence the server never claimed.
   */
  withheld?: string | null;
}

/** The server's refusal of a run this caller CAUSED and may not read back (#426 r25). */
export const RUN_WITHHELD = 'run_withheld';

export interface RunRefusal {
  code: string;
  message: string;
}

const unwrap = <T,>(res: any): T => {
  const body = res?.data;
  if (body && body.success === false) {
    throw new Error(body.error || 'Request failed');
  }
  return (body?.data ?? body) as T;
};

export async function listWorkflows(): Promise<WorkflowSummary[]> {
  return unwrap(await apiClient.get('/api/workflows'));
}

export async function getWorkflow(id: string): Promise<Workflow> {
  return unwrap(await apiClient.get(`/api/workflows/${id}`));
}

export async function createWorkflow(body: Partial<Workflow>): Promise<Workflow> {
  return unwrap(await apiClient.post('/api/workflows', body));
}

export async function updateWorkflow(id: string, body: Partial<Workflow>): Promise<Workflow> {
  return unwrap(await apiClient.put(`/api/workflows/${id}`, body));
}

export async function deleteWorkflow(id: string): Promise<void> {
  await apiClient.delete(`/api/workflows/${id}`);
}

export async function runWorkflow(id: string, parameters: Record<string, any> = {}): Promise<Execution> {
  return unwrap(await apiClient.post(`/api/workflows/${id}/run`, { parameters }, { timeout: 120000 }));
}

/**
 * The refusal in a failed run request, or null when it is an ordinary error (#426 r26).
 *
 * `POST /{id}/run` answers `403 {code: "run_withheld"}` when the run happened and its record belongs to
 * another account — the one case where the server cannot conceal, because the caller asked for the run.
 * Reading the code is how the interface tells that apart from "the request failed"; the sentence shown
 * is the server's own.
 */
export function runRefusal(err: any): RunRefusal | null {
  const body = err?.response?.data;
  const code = body?.code;
  if (typeof code !== 'string' || !code) return null;
  return { code, message: typeof body?.error === 'string' && body.error ? body.error : 'This run is not yours to read.' };
}

export async function getNodeTypes(): Promise<NodeTypeDescriptor[]> {
  return unwrap(await apiClient.get('/api/workflows/node-types'));
}

export async function listRuns(workflowId: string): Promise<Execution[]> {
  return unwrap(await apiClient.get(`/api/workflows/${workflowId}/runs`));
}

export async function getRun(executionId: string): Promise<Execution> {
  return unwrap(await apiClient.get(`/api/workflows/runs/${encodeURIComponent(executionId)}`));
}

export async function listWorkflowTemplates(): Promise<WorkflowTemplate[]> {
  return unwrap(await apiClient.get('/api/workflows/templates'));
}

export function pdfDownloadUrl(executionId: string, nodeId: string): string {
  return `/api/workflows/runs/${executionId}/nodes/${nodeId}/pdf`;
}
